import {
  Card,
  Flex,
  Heading,
  Tag,
  Text,
  UnstyledButton,
  VisuallyHidden,
} from '@buffer-mono/popcorn'
import type * as React from 'react'
import { useCallback } from 'react'
import type { Template } from '../../notion-sync/interface'

import styles from './TemplateMiniCard.module.css'
import { clsx } from 'clsx'
import { Link } from 'react-router-dom-v5-compat'
import { TemplateDialog } from '~publish/pages/Create/Templates/components/Template/TemplateDialog'
import { BufferTrackerReact } from '@buffer-mono/tracking-plan'
import { useOrganizationId } from '~publish/contexts/AccountContext'

type TemplateMiniCardProps = {
  template: Template
  variant?: 'large' | 'small'
  className?: string
  // TODO: creator-camp-cleanup
  creatorCampTag?: string
}

export const TemplateMiniCard = ({
  template,
  variant = 'large',
  className,
  // TODO: creator-camp-cleanup
  creatorCampTag,
}: TemplateMiniCardProps): JSX.Element => {
  const shouldShowTags = variant === 'large'

  const articleStyle: React.CSSProperties & Record<`--${string}`, string> = {
    '--template-color': template.color,
  }

  const textStyle: React.CSSProperties &
    Record<`--${string}`, string | number> = {
    '--line-clamp': creatorCampTag || template.tags.length > 0 ? 2 : 3,
  }

  return (
    <Card asChild>
      <article
        style={articleStyle}
        className={clsx(styles.cardContainer, className)}
      >
        <Flex className={clsx(styles.templateMiniCard, styles.grow)}>
          <div
            className={styles.templateMiniCardHeaderIcon}
            data-size={variant}
          >
            {template.emoji}
          </div>
          <Flex
            direction="column"
            justify="between"
            gap="xs"
            className={styles.grow}
          >
            <Flex direction="column" gap="2xs" className={styles.grow}>
              <Heading
                size={variant === 'large' ? 'medium' : 'xsmall'}
                className={styles.templateMiniCardTitle}
              >
                {template.title}
              </Heading>
              <Text
                color="subtle"
                size={variant === 'large' ? 'md' : 'sm'}
                className={styles.templateMiniCardDescription}
                style={textStyle}
              >
                {template.description}
              </Text>
            </Flex>
            {shouldShowTags && (
              <div className={styles.templateMiniCardTags}>
                {/* TODO: creator-camp-cleanup */}
                {creatorCampTag && (
                  <Tag
                    key="Creator Camp"
                    className={styles.templateMiniCardTag}
                  >
                    {creatorCampTag}
                  </Tag>
                )}
                {template.tags.map((tag) => (
                  <Tag
                    key={tag}
                    className={clsx(
                      styles.templateMiniCardTag,
                      styles[`tag-${tag}`],
                    )}
                  >
                    {tag}
                  </Tag>
                ))}
              </div>
            )}
          </Flex>
        </Flex>
      </article>
    </Card>
  )
}

export const TemplateMiniCardLink = ({
  templateId,
  cta,
  className,
  children,
}: {
  templateId: string
  cta: CTA
  className?: string
  children: React.ReactNode
}): JSX.Element => {
  return (
    <UnstyledButton
      id={`template-card-${templateId}`}
      as={Link}
      to={{
        pathname: `/create/templates/template/${templateId}`,
        search: `cta=${cta}`,
      }}
      className={className}
    >
      <VisuallyHidden>Open template</VisuallyHidden>
      {children}
    </UnstyledButton>
  )
}

export const TemplateMiniCardButton = ({
  template,
  cta,
  preselectChannelId,
  ...props
}: TemplateMiniCardProps & {
  cta: CTA
  preselectChannelId?: string
}): JSX.Element => {
  const organizationId = useOrganizationId()
  const trackOpenClick = useCallback(() => {
    BufferTrackerReact.templateClicked({
      cta: cta ?? 'create-templates-browse-list-1',
      organizationId: organizationId ?? '',
      product: 'publish',
      templateId: template.id,
    })
  }, [cta, organizationId, template.id])

  return (
    <TemplateDialog
      template={template}
      cta={cta}
      preselectChannelId={preselectChannelId}
    >
      <UnstyledButton onClick={trackOpenClick}>
        <VisuallyHidden>Open template</VisuallyHidden>
        <TemplateMiniCard template={template} {...props} />
      </UnstyledButton>
    </TemplateDialog>
  )
}
